import type { PermissionKey } from "@vaulted/shared";
import { type ReactNode, useEffect, useState } from "react";
import { post } from "../api";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useWaitingRequests } from "../pages/MobileRequests";
import { useCommCounts } from "../pages/Voicemail";
import { useSession } from "../session";
import { AvailabilityLight } from "./AvailabilityLight";
import { useBackTrail } from "./BackLink";
import { GfeSwitch } from "./Gfe";
import { IncomingCalls } from "./IncomingCalls";
import { isSettingsPath, useSettingsGroups } from "./SettingsLayout";
import { cx } from "./ui";
import { usePendingCharts } from "../pages/PendingCharts";

interface NavItem {
  to: string;
  label: string;
  needs?: PermissionKey[];
}

const NAV: NavItem[] = [
  { to: "/", label: "Today" },
  { to: "/front-desk", label: "Front desk", needs: ["appointments.manage", "mobile.confirm"] },
  { to: "/calendar", label: "Calendar", needs: ["appointments.manage"] },
  // Managers only: staff running an event day reach its check-in screen from Today and Front desk.
  { to: "/events", label: "Events", needs: ["events.manage"] },
  { to: "/clients", label: "Clients", needs: ["clients.view_all", "clients.view_assigned"] },
  { to: "/pending-charts", label: "Pending Charts", needs: ["charts.write", "charts.sign", "charts.view_all"] },
  { to: "/communication", label: "Communication", needs: ["calls.view"] },
  { to: "/reports", label: "Reports", needs: ["reports.financial"] },
  { to: "/payouts", label: "Payouts", needs: ["payouts.view_own", "reports.financial", "payouts.manage"] },
  { to: "/inventory", label: "Inventory", needs: ["inventory.view"] },
  { to: "/availability", label: "Availability", needs: ["availability.edit_own", "availability.view_team", "availability.manage"] },
];

export function Shell() {
  const { me, can, signOut } = useSession();
  const [open, setOpen] = useState(false);
  useBackTrail();
  if (!me) return null;
  // Settings goes to the first of its pages this person can open (Catalog, at least).
  const settings = useSettingsGroups()[0]?.items[0];
  const items = [...NAV.filter((n) => !n.needs || can(...n.needs)), ...(settings ? [{ to: settings.to, label: "Settings", settings: true }] : [])];
  const { pathname } = useLocation();
  const waiting = useWaitingRequests().data?.length ?? 0;
  const comm = useCommCounts().data;
  const openComm = (comm?.texts ?? 0) + (comm?.emails ?? 0) + (comm?.calls ?? 0);
  // Only what this person has to do (their charts to sign; the director's to review).
  const pendingCharts = usePendingCharts().data?.count ?? 0;

  const nav = (
    <nav className="flex flex-col gap-0.5" aria-label="Main">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            cx(
              "relative rounded-[var(--radius-control)] px-3 py-2 text-[15px] font-medium transition-colors",
              ("settings" in item ? isSettingsPath(pathname) : isActive) ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white",
            )
          }
        >
          {({ isActive: linkActive }) => {
            const isActive = "settings" in item ? isSettingsPath(pathname) : linkActive;
            return (
            <>
              {isActive && <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-brand" aria-hidden />}
              {item.label}
              {item.to === "/front-desk" && waiting > 0 && (
                <span className="ml-2 rounded-full bg-brand px-1.5 py-0.5 text-xs font-semibold text-white" aria-label={`${waiting} waiting`}>
                  {waiting}
                </span>
              )}
              {item.to === "/pending-charts" && pendingCharts > 0 && (
                <span className="ml-2 rounded-full bg-brand px-1.5 py-0.5 text-xs font-semibold text-white" aria-label={`${pendingCharts} for you`}>
                  {pendingCharts}
                </span>
              )}
              {item.to === "/communication" && openComm > 0 && (
                <span className="ml-2 rounded-full bg-brand px-1.5 py-0.5 text-xs font-semibold text-white" aria-label={`${openComm} need an answer`}>
                  {openComm}
                </span>
              )}
            </>
            );
          }}
        </NavLink>
      ))}
    </nav>
  );

  // Only in test environments (staging): a small tag; what it means is on hover.
  const testMode = me.testMode ? (
    <span className="self-start rounded-full bg-[#fbbf24] px-2 py-0.5 text-[11px] font-bold tracking-wide text-ink uppercase" title={me.testMode}>
      Test Mode
    </span>
  ) : null;

  // Unanswered texts and emails, always in view (sidebar on a computer, top bar on a phone).
  const inbox = can("calls.view") ? (
    <div className="flex gap-1.5">
      <InboxCount to="/communication" label="Texts" n={comm?.texts ?? 0} icon={<path d="M4 5h16v11H8l-4 4V5z" />} onClick={() => setOpen(false)} />
      <InboxCount to="/communication" label="Emails" n={comm?.emails ?? 0} icon={<path d="M3 6h18v12H3zM3 7l9 6 9-6" />} onClick={() => setOpen(false)} />
    </div>
  ) : null;

  // Booking right now and Qualiphy auto-send, as part of the menu.
  const status = (
    <div className="flex flex-col gap-0.5 rounded-[var(--radius-panel)] bg-white/[0.04] p-1 ring-1 ring-white/10 empty:hidden">
      <AvailabilityLight />
      <GfeSwitch />
    </div>
  );

  const brand = (
    <div className="flex items-center gap-3">
      {me.settings.logoUrl ? (
        <img src={me.settings.logoUrl} alt="" className="size-9 rounded-lg bg-white object-contain p-1" />
      ) : (
        <img src="/favicon.svg" alt="" className="size-9" />
      )}
      <div className="min-w-0">
        <div className="truncate font-display text-lg leading-tight text-white">{me.clinic.name}</div>
      </div>
    </div>
  );

  const account = (
    <div className="border-t border-white/10 pt-4">
      <NavLink
        to="/account"
        onClick={() => setOpen(false)}
        className={({ isActive }) =>
          cx("block rounded-[var(--radius-control)] px-3 py-2", isActive ? "bg-white/10" : "hover:bg-white/5")
        }
      >
        <div className="truncate text-sm font-semibold text-white">
          {me.staff.firstName} {me.staff.lastName}
        </div>
        <div className="truncate text-xs text-white/60">Your account and sign-in</div>
      </NavLink>
      <button onClick={signOut} className="mt-1 w-full rounded-[var(--radius-control)] px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 hover:text-white">
        Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[256px_1fr] print:block">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-dvh print:hidden flex-col justify-between overflow-y-auto bg-ink px-4 py-5 lg:flex">
        <div className="flex flex-col gap-5">
          {testMode}
          {brand}
          {inbox}
          {status}
          {nav}
        </div>
        {account}
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex items-center justify-between gap-3 bg-ink px-4 py-3 lg:hidden print:hidden">
        <div className="flex min-w-0 items-center gap-3">
          {brand}
          {testMode}
        </div>
        <div className="ml-auto">{inbox}</div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="rounded-md p-2 text-white hover:bg-white/10"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="1.8">
            {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>
      {open && (
        <div className="fixed inset-x-0 top-[60px] bottom-0 z-20 flex flex-col justify-between gap-4 overflow-y-auto bg-ink px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-4">
            {status}
            {nav}
          </div>
          {account}
        </div>
      )}

      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 lg:py-10">
        {me.viewingAs && <ViewingAsBar name={`${me.staff.firstName} ${me.staff.lastName}`} until={me.viewingAs.until} tz={me.clinic.timezone} />}
        <Outlet />
      </main>
      <IncomingCalls />
    </div>
  );
}

/** A count that stands out when something's waiting ("Emails 3"), quiet at zero. */
function InboxCount({ to, label, n, icon, onClick }: { to: string; label: string; n: number; icon: ReactNode; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      title={n ? `${n} ${label.toLowerCase()} waiting for an answer` : `No ${label.toLowerCase()} waiting`}
      aria-label={`${label}: ${n} waiting`}
      className={cx(
        "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold transition-colors",
        n ? "bg-brand text-white shadow-[0_0_0_3px_rgba(255,255,255,0.12)] hover:brightness-110" : "bg-white/[0.06] text-white/60 hover:bg-white/10",
      )}
    >
      <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
        {icon}
      </svg>
      <span className="hidden sm:inline lg:inline">{label}</span>
      <span>{n}</span>
    </Link>
  );
}

/** "View as": who you're seeing the app as, that it's view only, when it ends, and a way back to your own view. */
function ViewingAsBar({ name, until, tz }: { name: string; until: string; tz: string }) {
  const [stopping, setStopping] = useState(false);
  const back = () => window.location.assign("/staff");
  // Back to your own view when the time is up.
  useEffect(() => {
    const t = window.setTimeout(back, Math.max(0, new Date(until).getTime() - Date.now()) + 1000);
    return () => window.clearTimeout(t);
  }, [until]);
  const stop = async () => {
    setStopping(true);
    await post("/api/view-as/stop").catch(() => undefined);
    back();
  };
  const ends = new Date(until).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz });
  return (
    <div className="sticky top-2 z-40 mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-panel)] bg-[#7c3aed] px-4 py-2.5 text-sm text-white shadow print:hidden">
      <span>
        <strong>Viewing as {name}</strong> · view only, nothing can be changed · ends {ends}
      </span>
      <button type="button" onClick={stop} disabled={stopping} className="rounded-[var(--radius-control)] bg-white px-3 py-1 font-semibold text-[#7c3aed] hover:bg-white/90">
        {stopping ? "Stopping…" : "Stop viewing"}
      </button>
    </div>
  );
}
